import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, Shield, ShieldAlert, ShieldCheck, ExternalLink, Loader2, ClipboardList, Upload } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import * as takata from '../services/takataService';
import { VerificaTakata, EsitoTakata } from '../services/takataService';
import DatiDocumento from '../components/takata/DatiDocumento';
import IstruzioniEstensione, { useEstensionePresente } from '../components/takata/IstruzioniEstensione';

const MIT_URL = 'https://www.mit.gov.it/documentazione/campagne-di-richiamo-airbag-takata-verifica-ora-se-il-tuo-veicolo-e-coinvolto';

function normalizeVinClient(raw: string) {
  return (raw || '').toUpperCase().replace(/[\s\-_.]/g, '');
}

const ESITO_STYLE: Record<EsitoTakata, { bg: string; text: string; label: string }> = {
  non_coinvolto: { bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-300', text: 'text-blue-700 dark:text-blue-300', label: 'NON COINVOLTO nel richiamo Takata' },
  altri_richiami: { bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-300', text: 'text-blue-700 dark:text-blue-300', label: 'NON COINVOLTO (altri richiami segnalati)' },
  richiamo: { bg: 'bg-red-50 dark:bg-red-900/20 border-red-400', text: 'text-red-700 dark:text-red-300', label: 'COINVOLTO nel richiamo Takata' },
  stop_drive: { bg: 'bg-red-100 dark:bg-red-900/30 border-red-600', text: 'text-red-800 dark:text-red-200', label: 'COINVOLTO - STOP DRIVE: non utilizzare il veicolo' },
  non_soggetto: { bg: 'bg-gray-50 dark:bg-gray-800 border-gray-300', text: 'text-gray-700 dark:text-gray-300', label: 'Marca non compresa nell\'elenco del Ministero' },
  fonte_non_disponibile: { bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-400', text: 'text-amber-700 dark:text-amber-300', label: 'Fonte ufficiale non disponibile' },
};

const TakataVerificaPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [vin, setVin] = useState('');
  const [marca, setMarca] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<string | null>(null);
  const [portaleUrl, setPortaleUrl] = useState<string | null>(null);
  const [mitUrl, setMitUrl] = useState<string | null>(null);

  // captcha relay
  const [relayId, setRelayId] = useState<string | null>(null);
  const [captchaImage, setCaptchaImage] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState('');

  // manuale (guidato)
  const [esitoManual, setEsitoManual] = useState<EsitoTakata>('non_coinvolto');
  const [codiceCampagna, setCodiceCampagna] = useState('');
  const [numeroPraticaSTA, setNumeroPraticaSTA] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);

  // risultato
  const [verifica, setVerifica] = useState<VerificaTakata | null>(null);

  // I dati che completano i documenti (riferimenti pratica e acquirente) stanno nel
  // componente DatiDocumento, condiviso con il registro: qui non servono stati propri.

  const extPresent = useEstensionePresente();

  // Questa pagina non è più la porta d'ingresso degli associati: dal 29/07/2026 si lavora
  // con l'estensione, e il registro è la prima cosa che si vede. Il modulo resta per chi
  // deve provare i portali. Finché il permesso non è noto non si mostra nulla, altrimenti
  // comparirebbe per un istante anche a chi non deve vederlo.
  const [accessoConsentito, setAccessoConsentito] = useState<boolean | null>(null);
  useEffect(() => {
    let vivo = true;
    takata.getAccessoModuloWeb()
      .then((r) => { if (vivo) setAccessoConsentito(!!r.consentito); })
      .catch(() => { if (vivo) setAccessoConsentito(false); });
    return () => { vivo = false; };
  }, []);
  useEffect(() => {
    if (accessoConsentito === false) navigate('/area-riservata/takata', { replace: true });
  }, [accessoConsentito, navigate]);

  const vinNorm = normalizeVinClient(vin);
  const vinValid = vinNorm.length === 17 && !/[IOQ]/.test(vinNorm);

  const reset = () => {
    setMode(null); setPortaleUrl(null); setMitUrl(null);
    setRelayId(null); setCaptchaImage(null); setCaptcha('');
    setVerifica(null); setScreenshot(null); setCodiceCampagna('');
    setEsitoManual('non_coinvolto');
    // I dati del documento si azzerano da sé: DatiDocumento riparte dalla verifica che
    // riceve, quindi non può restare precompilato l'acquirente di un altro veicolo.
    // Il numero di pratica NON si azzera qui: viene indicato PRIMA di verificare e
    // appartiene alla verifica in corso. Lo svuota il pulsante «Nuova verifica», che è il
    // momento in cui l'operatore dichiara di passare a un altro veicolo.
  };

  // Avvia la verifica guidata tramite l'estensione (apre il portale, compila, cattura).
  const openWithExtension = () => {
    if (!document.documentElement.hasAttribute('data-unasca-takata-ext')) {
      toast.error('Estensione UNASCA non rilevata. Installala per la verifica guidata automatica.');
      return;
    }
    window.postMessage({ source: 'unasca-takata-card', type: 'GUIDED_START', vin: vinNorm }, window.location.origin);
    toast.success('Apro il portale del costruttore con l\'estensione…');
  };

  const handleVerify = async () => {
    if (!vinValid) { toast.error('Inserisci un numero di telaio (VIN) valido di 17 caratteri.'); return; }
    setLoading(true);
    reset();
    try {
      const r = await takata.verify(vinNorm, marca || undefined, numeroPraticaSTA || undefined);
      if (r.marca && !marca) setMarca(r.marca);
      if (r.done && r.verifica) { setVerifica(r.verifica); setMode('done'); return; }
      setMode(r.mode || 'non_disponibile');
      setPortaleUrl(r.portaleUrl || null);
      setMitUrl(r.mitUrl || null);
      if (r.mode === 'needs_captcha') {
        await startRelay(r.marca || marca);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Errore durante la verifica');
    } finally {
      setLoading(false);
    }
  };

  const startRelay = async (m: string) => {
    try {
      const r = await takata.relayStart(vinNorm, m, numeroPraticaSTA || undefined);
      setRelayId(r.relayId);
      setCaptchaImage(r.captchaImage);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Portale non raggiungibile. Usa la modalità guidata.');
      setMitUrl(MIT_URL);
      setMode('non_disponibile');
    }
  };

  const handleRelaySubmit = async () => {
    if (!relayId || !captcha.trim()) return;
    setLoading(true);
    try {
      const r = await takata.relaySubmit(relayId, captcha.trim());
      if (r.retry) {
        toast.error(r.message || 'Codice errato, riprova');
        if (r.captchaImage) setCaptchaImage(r.captchaImage);
        setCaptcha('');
      } else if (r.done && r.verifica) {
        setVerifica(r.verifica); setMode('done');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Errore durante l\'invio');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    setLoading(true);
    try {
      const r = await takata.verifyManual({
        vin: vinNorm, marca, esito: esitoManual, codiceCampagna,
        numeroPraticaSTA, screenshot,
      });
      if (r.verifica) { setVerifica(r.verifica); setMode('done'); }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const coinvolto = verifica ? takata.isCoinvolto(verifica.esito) : false;

  // Finché non sappiamo se questa pagina spetta a chi la sta aprendo non si disegna nulla:
  // mostrarla e poi toglierla farebbe intravedere un modulo che non deve essere usato.
  if (accessoConsentito !== true) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate('/area-riservata')} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Torna all'area riservata
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Verifica richiamo airbag Takata</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Controllo dal numero di telaio + generazione dichiarazione</p>
          </div>
          <button onClick={() => navigate('/area-riservata/takata/stato-portali')} className="ml-auto flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:shadow">
            <Activity className="w-4 h-4" /> Stato portali
          </button>
          <button onClick={() => navigate('/area-riservata/takata')} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:shadow">
            <ClipboardList className="w-4 h-4" /> Registro
          </button>
        </div>

        <IstruzioniEstensione />

        {/* Input VIN */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
          <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">Numero di telaio (VIN)</label>
          <input
            value={vin}
            onChange={(e) => setVin(e.target.value)}
            placeholder="Es. WVGZZZA1ZKV025912"
            maxLength={25}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white uppercase tracking-wide"
          />
          <div className="flex items-center justify-between mt-1">
            <span className={`text-xs ${vinNorm.length === 17 ? 'text-green-600' : 'text-gray-400'}`}>{vinNorm.length}/17</span>
            {vinNorm.length === 17 && /[IOQ]/.test(vinNorm) && <span className="text-xs text-red-500">Contiene I/O/Q: ricontrolla</span>}
          </div>

          <label className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-3 block">Marca (suggerita dal telaio, modificabile)</label>
          <input
            value={marca}
            onChange={(e) => setMarca(e.target.value)}
            placeholder="Rilevata automaticamente"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />

          {/* Indicato PRIMA di verificare, quando l'operatore ha la pratica davanti: cosi'
              il certificato esce gia' col riferimento e col nome file giusto, senza dover
              rientrare dal registro. Resta modificabile da li' in ogni momento. */}
          <label className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-3 block">Numero pratica STA (opzionale)</label>
          <input
            value={numeroPraticaSTA}
            onChange={(e) => setNumeroPraticaSTA(e.target.value)}
            placeholder="Es. 000375T26"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
          <p className="text-2xs text-gray-500 dark:text-gray-400 mt-1">
            Compare sul certificato e davanti al nome del file scaricato.
          </p>

          <button
            onClick={handleVerify}
            disabled={loading || !vinValid}
            className="mt-4 w-full py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-orange-600 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verifica
          </button>

          {extPresent && vinValid && (
            <button
              onClick={openWithExtension}
              className="mt-2 w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-4 h-4" /> Apri portale e verifica (estensione)
            </button>
          )}
        </div>

        {/* Marca non riconosciuta dal telaio */}
        {mode === 'marca_sconosciuta' && !verifica && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 rounded-xl p-4 mb-4">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Non è stato possibile riconoscere la marca dal numero di telaio. Inserisci la marca nel campo qui sopra e premi di nuovo <strong>Verifica</strong>.
            </p>
          </div>
        )}

        {/* Captcha relay (UNRAE) */}
        {mode === 'needs_captcha' && captchaImage && !verifica && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Digita i caratteri mostrati nell'immagine (fonte ufficiale UNRAE):</p>
            <img src={captchaImage} alt="captcha" className="border rounded mb-2 bg-white" />
            <div className="flex gap-2">
              <input value={captcha} onChange={(e) => setCaptcha(e.target.value)} placeholder="Codice"
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white" />
              <button onClick={handleRelaySubmit} disabled={loading} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Invia'}
              </button>
            </div>
          </div>
        )}

        {/* Guidato / non disponibile: link + form manuale */}
        {(mode === 'guided' || mode === 'non_disponibile') && !verifica && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
            {mode === 'guided' && portaleUrl && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Questo marchio si verifica sul portale ufficiale. Aprilo, controlla il telaio, poi registra qui l'esito allegando lo screenshot.
              </p>
            )}
            {mode === 'non_disponibile' && (
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                Fonte automatica non disponibile per questa marca. Consulta l'elenco ufficiale del MIT, verifica manualmente e registra l'esito.
              </p>
            )}
            <a href={portaleUrl || mitUrl || '#'} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 mb-4">
              <ExternalLink className="w-4 h-4" /> {portaleUrl ? 'Apri il portale ufficiale' : 'Apri l\'elenco MIT'}
            </a>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Esito</label>
                <select value={esitoManual} onChange={(e) => setEsitoManual(e.target.value as EsitoTakata)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
                  <option value="non_coinvolto">Non coinvolto</option>
                  <option value="altri_richiami">Altri richiami (non airbag)</option>
                  <option value="richiamo">Coinvolto (richiamo)</option>
                  <option value="stop_drive">Coinvolto - Stop Drive</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Codice campagna (se presente)</label>
                <input value={codiceCampagna} onChange={(e) => setCodiceCampagna(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm" />
              </div>
            </div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mt-3 block flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" /> Screenshot dell'esito (prova)
            </label>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setScreenshot(e.target.files?.[0] || null)}
              className="mt-1 w-full text-sm text-gray-600 dark:text-gray-400" />
            <button onClick={handleManualSubmit} disabled={loading} className="mt-4 w-full py-2.5 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
              {loading ? 'Salvataggio…' : 'Registra esito'}
            </button>
          </div>
        )}

        {/* Risultato */}
        {verifica && (
          <div className={`rounded-xl border-2 p-5 mb-4 ${ESITO_STYLE[verifica.esito].bg}`}>
            <div className="flex items-center gap-2 mb-1">
              {coinvolto ? <ShieldAlert className="w-5 h-5 text-red-600" /> : <ShieldCheck className="w-5 h-5 text-blue-600" />}
              <span className={`font-bold ${ESITO_STYLE[verifica.esito].text}`}>{ESITO_STYLE[verifica.esito].label}</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {verifica.marca} · {verifica.vin} · protocollo {verifica.protocollo}
              {verifica.codiceCampagna ? ` · codice ${verifica.codiceCampagna}` : ''}
            </p>

            {!takata.haCertificato(verifica.esito) && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">{takata.TESTO_NON_SOGGETTO}</p>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              {takata.haCertificato(verifica.esito) && (
                <button onClick={() => takata.scaricaCertificato(verifica)} className="px-3 py-2 text-sm rounded-lg bg-gray-900 dark:bg-gray-700 text-white font-medium">
                  Scarica certificato
                </button>
              )}
              {coinvolto && (
                <button onClick={() => takata.scaricaDichiarazione(verifica)} className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white font-medium">
                  Scarica dichiarazione
                </button>
              )}
              <button onClick={() => { setVin(''); setMarca(''); setNumeroPraticaSTA(''); reset(); }} className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                Nuova verifica
              </button>
            </div>
          </div>
        )}

        {/* I dati che completano i documenti. Sono in un componente condiviso con il
            registro: nel flusso con l'estensione questa pagina non si vede nemmeno (a
            verifica conclusa il certificato si scarica da sé e si finisce nel registro),
            quindi gli stessi campi devono essere raggiungibili anche da lì. */}
        {verifica && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <DatiDocumento verifica={verifica} onSaved={setVerifica} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TakataVerificaPage;
